// src/components/HomePageEditor.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import {
    doc,
    onSnapshot,
    setDoc,
    serverTimestamp,
} from 'firebase/firestore';
import {
    ref,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject,
} from 'firebase/storage';
import { useAuth } from '../AuthProvider';
import '../styles/BellaBizarreStyle.css';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * Firestore document layout:
 *   Collection: siteContent
 *   Doc:        home
 *   Fields:
 *     quote: string
 *     featureImageUrl: string
 *     featureImagePath: string  (Storage path for clean replacement/deletion)
 *     updatedAt: serverTimestamp
 *     updatedBy: user uid
 */
export default function HomePageEditor() {
    const { user } = useAuth();

    const [quote, setQuote] = useState('');
    const [featureImageUrl, setFeatureImageUrl] = useState('');
    const [featureImagePath, setFeatureImagePath] = useState('');
    const [newFile, setNewFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(null);
    const [message, setMessage] = useState('');
    const fileRef = useRef(null);

    // Load current content in realtime
    useEffect(() => {
        const d = doc(db, 'siteContent', 'home');
        const unsub = onSnapshot(d, (snap) => {
            const data = snap.data();
            if (data) {
                setQuote(data.quote || '');
                setFeatureImageUrl(data.featureImageUrl || '');
                setFeatureImagePath(data.featureImagePath || '');
            }
        });
        return unsub;
    }, []);

    if (!user) {
        return (
            <div className="event-uploader-container">
                <p>Please <Link to="/login">log in</Link> to manage the home page.</p>
            </div>
        );
    }

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f && f.size > MAX_FILE_SIZE) {
            setMessage('File exceeds 100MB');
            if (fileRef.current) fileRef.current.value = '';
            return;
        }
        setNewFile(f || null);
        setMessage('');
    };

    const removeImage = async () => {
        if (!featureImagePath) {
            setFeatureImageUrl('');
            await setDoc(
                doc(db, 'siteContent', 'home'),
                { featureImageUrl: '', featureImagePath: '', updatedAt: serverTimestamp(), updatedBy: user.uid },
                { merge: true }
            );
            return;
        }
        if (!window.confirm('Remove current feature image?')) return;
        try {
            const r = ref(storage, featureImagePath);
            await deleteObject(r);
            await setDoc(
                doc(db, 'siteContent', 'home'),
                { featureImageUrl: '', featureImagePath: '', updatedAt: serverTimestamp(), updatedBy: user.uid },
                { merge: true }
            );
            setFeatureImageUrl('');
            setFeatureImagePath('');
            setMessage('Image removed.');
        } catch (err) {
            console.error(err);
            setMessage('Failed to remove image.');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        setProgress(null);

        let finalUrl = featureImageUrl;
        let finalPath = featureImagePath;

        try {
            // If a new file is chosen, upload it and (optionally) delete previous
            if (newFile) {
                // Delete old file if it exists
                if (finalPath) {
                    try { await deleteObject(ref(storage, finalPath)); } catch (_) {}
                }
                const path = `site/home/feature_${Date.now()}_${newFile.name}`;
                const storageRef = ref(storage, path);
                const task = uploadBytesResumable(storageRef, newFile, { contentType: newFile.type });
                await new Promise((resolve, reject) => {
                    task.on('state_changed',
                        (snap) => {
                            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
                            setProgress(pct);
                        },
                        reject,
                        async () => {
                            finalUrl = await getDownloadURL(task.snapshot.ref);
                            finalPath = path;
                            resolve();
                        }
                    );
                });
            }

            await setDoc(
                doc(db, 'siteContent', 'home'),
                {
                    quote: (quote || '').trim(),
                    featureImageUrl: finalUrl || '',
                    featureImagePath: finalPath || '',
                    updatedAt: serverTimestamp(),
                    updatedBy: user.uid,
                },
                { merge: true }
            );

            setMessage('Home page updated.');
            setNewFile(null);
            if (fileRef.current) fileRef.current.value = '';
        } catch (err) {
            console.error(err);
            setMessage(err.message || 'Save failed.');
        } finally {
            setSaving(false);
            setProgress(null);
        }
    };

    return (
        <div className="event-uploader-container">
            <h2>Home Page Editor</h2>

            <form onSubmit={handleSave} className="event-uploader-form">
                <label>
                    Quote (shown on the front page):
                    <textarea
                        value={quote}
                        onChange={(e) => setQuote(e.target.value)}
                        placeholder="Enter a short quote or tagline…"
                        rows={3}
                    />
                </label>

                <label>
                    Feature Image:
                    <input type="file" accept="image/*" onChange={handleFileChange} ref={fileRef} />
                </label>

                {progress !== null && <p>Uploading: {progress}%</p>}

                {featureImageUrl && (
                    <div style={{ margin: '8px 0' }}>
                        <img src={featureImageUrl} alt="Current feature" className="gallery-image" />
                        <div className="event-uploader-card-buttons" style={{ marginTop: 8 }}>
                            <button type="button" className="event-uploader-delete-button" onClick={removeImage}>
                                Remove Image
                            </button>
                        </div>
                    </div>
                )}

                {message && <p className="event-uploader-message">{message}</p>}

                <button type="submit" disabled={saving} className="event-uploader-submit-button">
                    {saving ? 'Saving…' : 'Save Changes'}
                </button>
            </form>
        </div>
    );
}
